import { __spreadArray, __assign } from 'tslib';
import { useCallback } from 'react';
import { ImportStatus } from '@keepeek/commons';
import { useSetRecoilState } from 'recoil';
import { scheduleFileDescriptorRefresh } from '../../../Upload/utils.js';
import { openUploadDialogAtom, uploadFileDescriptorsAtom, kpkMediaImportsAtom, kpkMediaImportsProgressAtom } from '../atoms.js';
import { convertStatus } from '../utils.js';

/**
 * Upload update state hook.
 */
var useUploadUpdate = function (uploadKey) {
    var setOpenUploadDialog = useSetRecoilState(openUploadDialogAtom(uploadKey));
    var setUploadFileDescriptor = useSetRecoilState(uploadFileDescriptorsAtom(uploadKey));
    var setKpkMediaImportsAtom = useSetRecoilState(kpkMediaImportsAtom(uploadKey));
    var setKpkMediaImportsProgressAtom = useSetRecoilState(kpkMediaImportsProgressAtom(uploadKey));
    var fileDescriptorAdded = useCallback(function (file) {
        // Schecule descriptor refresh, do not refresh descriptor in state as it will
        // trigger useless renders
        var descriptorRefreshInterval = scheduleFileDescriptorRefresh(file.descriptor);
        var uploadFile = {
            descriptorId: file.descriptor.id || "",
            descriptorRefreshInterval: descriptorRefreshInterval
        };
        setUploadFileDescriptor(function (uploadFiles) { return __spreadArray(__spreadArray([], uploadFiles, true), [uploadFile], false); });
    }, [setUploadFileDescriptor]);
    var filesAdded = useCallback(function (files) {
        var kpkMediaImport = Array.from(files).map(function (file) {
            var _a;
            return {
                uuid: ((_a = file.upload) === null || _a === void 0 ? void 0 : _a.uuid) || "",
                originalFileName: file.name,
                mediaType: file.type,
                previewUrl: file.dataURL || "",
                descriptorHref: "",
                status: ImportStatus.ADDED,
                progress: 0
            };
        });
        setKpkMediaImportsAtom(function (kpkMediaImports) { return __spreadArray(__spreadArray([], kpkMediaImports, true), kpkMediaImport, true); });
        setKpkMediaImportsProgressAtom(function (kpkMediaImports) { return __spreadArray(__spreadArray([], kpkMediaImports, true), kpkMediaImport, true); });
    }, [setKpkMediaImportsAtom, setKpkMediaImportsProgressAtom]);
    var fileUpdate = useCallback(function (file, progress) {
        setKpkMediaImportsProgressAtom(function (kpkMediaImports) {
            return updateKpkMediaImport(kpkMediaImports, file, progress ? Math.round(progress) : 0);
        });
    }, [setKpkMediaImportsProgressAtom]);
    var filesCompleted = useCallback(function (files) {
        var kpkMediaImports = Array.from(files).map(function (file) {
            var _a, _b, _c, _d, _e;
            return {
                uuid: ((_a = file.upload) === null || _a === void 0 ? void 0 : _a.uuid) || "",
                originalFileName: file.name,
                mediaType: file.type,
                previewUrl: file.dataURL || "",
                descriptorHref: ((_d = (_c = (_b = file.descriptor) === null || _b === void 0 ? void 0 : _b._links) === null || _c === void 0 ? void 0 : _c.self) === null || _d === void 0 ? void 0 : _d.href) || "",
                status: convertStatus(file.status),
                progress: (_e = file.upload) === null || _e === void 0 ? void 0 : _e.progress
            };
        });
        // Update both atoms, so indexation process can be done with accurate information
        setKpkMediaImportsAtom(kpkMediaImports);
        setKpkMediaImportsProgressAtom(kpkMediaImports);
    }, [setKpkMediaImportsAtom, setKpkMediaImportsProgressAtom]);
    return {
        filesAdded: filesAdded,
        fileUpdate: fileUpdate,
        filesCompleted: filesCompleted,
        fileDescriptorAdded: fileDescriptorAdded,
        setOpenUploadDialog: setOpenUploadDialog
    };
};
function updateKpkMediaImport(kpkMediaImports, file, progress) {
    var _a, _b, _c, _d;
    if (!((_a = file.upload) === null || _a === void 0 ? void 0 : _a.uuid)) {
        return kpkMediaImports;
    }
    var index = kpkMediaImports.findIndex(function (kpkMediaImport) { var _a; return kpkMediaImport.uuid === ((_a = file.upload) === null || _a === void 0 ? void 0 : _a.uuid); });
    // Do nothing if file not added
    if (index < 0) {
        return kpkMediaImports;
    }
    // Keep array references
    var result = Array.from(kpkMediaImports);
    result[index] = __assign(__assign({}, result[index]), { previewUrl: file.dataURL || "", descriptorHref: ((_d = (_c = (_b = file.descriptor) === null || _b === void 0 ? void 0 : _b._links) === null || _c === void 0 ? void 0 : _c.self) === null || _d === void 0 ? void 0 : _d.href) || "", status: convertStatus(file.status, progress), progress: progress });
    return result;
}

export { useUploadUpdate };
//# sourceMappingURL=useUploadUpdate.js.map
